import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { v4 as uuidv4 } from 'uuid';
import { useSaveAiWorkerUsageMutation, useGetAiWorkersUsageByCategoryMutation } from '@circuitry-ai/doc-data';
import CtyPromptInput, { promptInputType, ErrorBoundary, CtyShowToast } from '@circuitry-ai/doc-ui';
import { FormConfig, FormGenieWorker } from '../types';
import { parseFormConfig } from '../utils';
import { FormRenderer } from './FormRenderer';
import { startAgentStream, streamAgentChat } from '../../../../../modules/chatadvisor/src/lib/agentconverse/agentstreamingservice';
import { CommentModal } from '../../../uxgenie/components/shared/CommentModal';
import type { InteractionState } from '../../../uxgenie/types';
import { formatUTCDateTime } from '../../../uxgenie/utils';

// Hoist ErrorBoundary wrap to module scope (never inside render)
const CtyPromptInputWithErrorBoundary = ErrorBoundary(CtyPromptInput);

const CLICKABLE_TAGS = ['LABEL', 'SPAN', 'P', 'H1', 'H2', 'H3', 'H4', 'TD', 'TH', 'BUTTON', 'LI'];

interface FormGenieSplitPanelProps {
  isEdit: boolean;
  isReadOnly: boolean;
  initialFormConfig?: FormConfig;
  initialSchemaInput?: string;
  initialFormName?: string | null;
  initialUsageId?: string;
  token: string;
  worker: FormGenieWorker | null;
  onSaveComplete: (workerId: string) => void;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: React.ReactNode | string;
  timestamp: string;
  elementContext?: string;
}

interface GenerationProgress {
  status: 'in_progress' | 'done' | 'error';
  nodes: string[];
  completedNodes: string[];
  errorMsg?: string;
}

export function FormGenieSplitPanel({
  isEdit,
  isReadOnly,
  initialFormConfig,
  initialSchemaInput,
  initialFormName,
  initialUsageId,
  worker,
  token,
  onSaveComplete,
}: FormGenieSplitPanelProps) {
  const [formConfig, setFormConfig] = useState<FormConfig | null>(initialFormConfig ?? null);
  const [viewingFormConfig, setViewingFormConfig] = useState<FormConfig | null>(initialFormConfig ?? null);
  const [messageFormConfigs, setMessageFormConfigs] = useState<Record<string, FormConfig>>({});
  const [messages, setMessages] = useState<Message[]>([]);
  const [generationProgress, setGenerationProgress] = useState<Record<string, GenerationProgress>>({});
  const [sessionId, setSessionId] = useState('');
  const streamStartedRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isSpeechMode, setIsSpeechMode] = useState(false);
  const lastUserInputRef = useRef(initialSchemaInput ?? '');
  const elementContextRef = useRef<string | undefined>(undefined);

  // Tracks the saved usage ID so the Share button can generate the PAL link
  const [savedUsageId, setSavedUsageId] = useState<string | undefined>(initialUsageId);

  // Element selection / comment state (edit mode only)
  const [interactionState, setInteractionState] = useState<InteractionState>({ isActive: false });
  const [instructionText, setInstructionText] = useState('');
  const [pendingInstructions, setPendingInstructions] = useState('');

  // Save modal
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [formName, setFormName] = useState(initialFormName ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const [saveAiWorkerUsage] = useSaveAiWorkerUsageMutation();
  const [getFormHistory] = useGetAiWorkersUsageByCategoryMutation();

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeContent = initialFormConfig
        ? 'Form loaded. You can chat to modify it, or save it as-is.'
        : 'Describe the form you need, or paste a Universal Schema JSON to generate a form.';
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: <p>{welcomeContent}</p>,
        timestamp: new Date().toISOString(),
      }]);
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Reset and init streaming session when worker changes
  useEffect(() => {
    if (!worker?.aiworker_id) return;
    streamStartedRef.current = false;
    setSessionId('');
  }, [worker?.aiworker_id]);

  useEffect(() => {
    let isCancelled = false;
    const init = async () => {
      if (streamStartedRef.current || sessionId || !token || !worker?.aiworker_id) return;
      streamStartedRef.current = true;
      try {
        const baseUrl = import.meta.env.CTY_AGENTS_STREAM_URL;
        const result = await startAgentStream({ baseUrl, aiworker_id: worker.aiworker_id, token });
        const session_id = result?.session_id;
        if (session_id && !isCancelled) setSessionId(session_id);
        else streamStartedRef.current = false;
      } catch {
        streamStartedRef.current = false;
      }
    };
    void init();
    return () => { isCancelled = true; };
  }, [token, worker?.aiworker_id, sessionId]);

  // -------------------------------------------------------------------------
  // Generate via streaming
  // -------------------------------------------------------------------------
  const handleGenerate = async (userInput: string) => {
    const messageId = uuidv4();
    const workerId = worker?.aiworker_id;
    const baseUrl = import.meta.env.CTY_AGENTS_STREAM_URL;

    setMessages(prev => [...prev, {
      id: messageId,
      role: 'assistant',
      content: 'Working on the form...',
      timestamp: new Date().toISOString(),
    }]);

    setGenerationProgress(prev => ({
      ...prev,
      [messageId]: { status: 'in_progress', nodes: [], completedNodes: [] },
    }));
    const streamedNodes: string[] = [];
    const streamedCompletedNodes: string[] = [];

    try {
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        const result = await startAgentStream({ baseUrl, aiworker_id: workerId, token });
        currentSessionId = result?.session_id || '';
        if (currentSessionId) setSessionId(currentSessionId);
        else throw new Error('Failed to initialize session. Please refresh and try again.');
      }

      let finalConfig: FormConfig | null = null;

      await streamAgentChat({
        baseUrl,
        aiworker_id: workerId,
        session_id: currentSessionId,
        user_input: isEdit && formConfig
          ? JSON.stringify(formConfig)
          : userInput,
        token,
        meta_data: {
          is_edit: isEdit,
          token,
          ...(isEdit ? { instructions: userInput } : {}),
          ...(isEdit && initialSchemaInput ? { original_data: initialSchemaInput } : {}),
        },
        onChunk: (chunk) => {
          if (chunk.event === 'node_started') {
            const nodeName = chunk.data?.title || chunk.data?.node_type || chunk.data?.node_id;
            if (nodeName && !streamedNodes.includes(nodeName)) {
              streamedNodes.push(nodeName);
              setGenerationProgress(prev => ({
                ...prev,
                [messageId]: { status: 'in_progress', nodes: [...streamedNodes], completedNodes: [...streamedCompletedNodes] },
              }));
            }
          }

          if (chunk.event === 'node_finished') {
            const finishedName = chunk.data?.title || chunk.data?.node_type || chunk.data?.node_id;
            if (finishedName && !streamedCompletedNodes.includes(finishedName)) {
              streamedCompletedNodes.push(finishedName);
              setGenerationProgress(prev => ({
                ...prev,
                [messageId]: { status: 'in_progress', nodes: [...streamedNodes], completedNodes: [...streamedCompletedNodes] },
              }));
            }
            const textVal = chunk.data?.outputs?.text;
            if (typeof textVal === 'string') {
              const parsed = parseFormConfig(textVal.trim());
              if (parsed) finalConfig = parsed;
            }
          }

          if (chunk.event === 'workflow_finished' || chunk.event === 'message') {
            const textVal = chunk.data?.outputs?.text ?? chunk.data?.text;
            if (typeof textVal === 'string') {
              const parsed = parseFormConfig(textVal.trim());
              if (parsed) finalConfig = parsed;
            }
          }
        },
        onError: (e) => { throw e; },
      });

      if (!finalConfig) throw new Error('No valid FormConfig JSON found in the AI response.');

      setFormConfig(finalConfig);
      setViewingFormConfig(finalConfig);
      setMessageFormConfigs(prev => ({ ...prev, [messageId]: finalConfig }));
      if (!formName && (finalConfig as FormConfig).formName) setFormName((finalConfig as FormConfig).formName);
      setGenerationProgress(prev => ({
        ...prev,
        [messageId]: { status: 'done', nodes: streamedNodes, completedNodes: [...streamedCompletedNodes] },
      }));
    } catch (error) {
      setGenerationProgress(prev => ({
        ...prev,
        [messageId]: {
          status: 'error',
          nodes: streamedNodes,
          completedNodes: [...streamedCompletedNodes],
          errorMsg: (error as Error).message,
        },
      }));
    }
  };

  const handleSendClick = async (text: string) => {
    if (!text.trim()) return;
    const ctx = elementContextRef.current;
    elementContextRef.current = undefined;
    lastUserInputRef.current = text;
    setMessages(prev => [...prev, {
      id: uuidv4(),
      role: 'user',
      content: text,
      elementContext: ctx,
      timestamp: new Date().toISOString(),
    }]);
    handleGenerate(text);
  };

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const getNextFormNumber = async (baseName: string, workerId: string): Promise<number> => {
    try {
      const response = await getFormHistory({ aiworker_id: workerId, aiworker_type: 'uxgenie_logs' }).unwrap();
      const forms = Array.isArray(response) ? response : (response as any)?.data || [];
      let maxNumber = 0;
      const escaped = baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`^${escaped}\\s*(\\d+)?$`, 'i');
      forms.forEach((f: any) => {
        const name = f?.output_data?.action_name || f?.agent_name || '';
        const match = name.match(pattern);
        if (match) {
          const num = match[1] ? parseInt(match[1], 10) : 0;
          if (num > maxNumber) maxNumber = num;
        }
      });
      return maxNumber + 1;
    } catch {
      return 1;
    }
  };

  const handleSave = () => {
    if (!formConfig) return;
    if (!formName && formConfig.formName) setFormName(formConfig.formName);
    setIsSaveModalOpen(true);
  };

  const confirmSave = async () => {
    if (!formConfig) return;
    const workerId = worker?.aiworker_id;
    if (!workerId) { CtyShowToast('error', 'No FormGenie worker selected.'); return; }

    setIsSaving(true);
    try {
      const usageId = uuidv4();
      const now = formatUTCDateTime(new Date());
      let nameToUse = formName.trim();
      if (!nameToUse) {
        const baseName = worker?.name || formConfig.formName || 'Form';
        const nextNumber = await getNextFormNumber(baseName, workerId);
        nameToUse = `${baseName} ${nextNumber}`;
      }

      await saveAiWorkerUsage({
        aiworker_id: workerId,
        payLoad: {
          aiworker_id: workerId,
          aiworker_usage_id: usageId,
          aiworker_type: 'uxgenie_logs',
          used_at: now,
          status: 'succeeded',
          input_data: { user_input: nameToUse, original_data: lastUserInputRef.current },
          output_data: {
            generated_code: JSON.stringify(formConfig),
            action_name: nameToUse,
            saved_at: now,
          },
        },
      }).unwrap();

      setSavedUsageId(usageId);
      setIsSaveModalOpen(false);
      CtyShowToast('success', `Form "${nameToUse}" saved successfully!`);
      onSaveComplete(workerId);
    } catch (error) {
      CtyShowToast('error', `Failed to save: ${(error as Error).message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Element selection / comment handlers (edit mode)
  // -------------------------------------------------------------------------
  const handlePreviewClick = useCallback((e: React.MouseEvent) => {
    if (!isEdit || isReadOnly) return;
    const target = e.target as HTMLElement;
    const text = target.textContent?.trim();
    if (!text) return;
    if (CLICKABLE_TAGS.includes(target.tagName) || target.childNodes.length === 1) {
      e.stopPropagation();
      setInteractionState({ isActive: true, selectedText: text, position: { x: e.clientX, y: e.clientY } });
    }
  }, [isEdit, isReadOnly]);

  const resetInteraction = useCallback(() => {
    setInteractionState({ isActive: false });
    setInstructionText('');
  }, []);

  const handleAddComment = () => {
    if (!interactionState.selectedText || !instructionText.trim()) return;
    const elementText = interactionState.selectedText;
    // Capture first selected element for message context chip
    if (!elementContextRef.current) {
      elementContextRef.current = elementText.substring(0, 40);
    }
    const truncated = elementText.length > 50 ? `${elementText.substring(0, 50)}...` : elementText;
    const line = `Change "${truncated}": ${instructionText.trim()}`;
    setPendingInstructions(prev => prev ? `${prev}\n${line}` : line);
    resetInteraction();
  };

  // -------------------------------------------------------------------------
  // Render: Left — Form Preview
  // -------------------------------------------------------------------------
  const renderPreviewPanel = () => (
    <div className="relative h-full min-h-0 bg-[#F2F4F7] p-2">
      {!isReadOnly && (
        <div className="absolute top-2 right-6 z-10">
          <Button
            color="primary"
            variant="solid"
            size="sm"
            radius="sm"
            isDisabled={!formConfig}
            onPress={handleSave}
            className="rounded-lg text-[12px] font-semibold"
          >
            Save
          </Button>
        </div>
      )}

      <div
        role={isEdit && !isReadOnly ? 'button' : undefined}
        tabIndex={isEdit && !isReadOnly ? 0 : undefined}
        className={`h-full bg-content1 rounded-2xl overflow-y-auto overflow-x-hidden ${isEdit && !isReadOnly ? 'cursor-crosshair' : ''}`}
        onClick={isEdit && !isReadOnly ? handlePreviewClick : undefined}
        onKeyDown={isEdit && !isReadOnly ? (e) => { if (e.key === 'Enter' || e.key === ' ') handlePreviewClick(e as any); } : undefined}
      >
        {!(viewingFormConfig ?? formConfig) ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="w-14 h-14 mb-4 bg-default-100 flex items-center justify-center rounded-xl">
              <Icon icon="mdi:form-select" className="text-default-400" width={28} />
            </div>
            <p className="text-base font-semibold text-[#444446]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Form Preview
            </p>
            <p className="text-xs text-default-500 mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Your generated form will appear here.
            </p>
          </div>
        ) : (
          <FormRenderer
            config={(viewingFormConfig ?? formConfig)!}
            uploadFile={() => { }}
            removeFile={() => { }}
            submitForm={(e) => console.log('[FormGenie] Form submitted:', e)}
            usageId={savedUsageId}
            className="h-full"
            hideActions={false}
          />
        )}
      </div>

      {/* Comment modal — appears at click position */}
      <CommentModal
        interactionState={interactionState}
        instructionText={instructionText}
        isEditMode={true}
        onInstructionChange={setInstructionText}
        onSubmit={handleAddComment}
        onClose={resetInteraction}
      />
    </div>
  );

  // -------------------------------------------------------------------------
  // Render: Right — Chat Panel
  // -------------------------------------------------------------------------
  const renderChatPanel = () => {
    const fontStyle = { fontFamily: 'Poppins, sans-serif' };

    const renderMessage = (msg: Message) => {
      if (msg.role === 'user') {
        return (
          <div key={msg.id} className="flex flex-col items-end gap-1 py-2">
            {msg.elementContext && (
              <span
                className="px-1.5 py-0.5 rounded-[6px] border text-xs"
                style={{ ...fontStyle, background: '#FDF2F8', borderColor: '#E66DB6', color: '#97005B' }}
              >
                {msg.elementContext}
              </span>
            )}
            <div
              className="max-w-[80%] rounded-2xl rounded-tr-sm px-4 py-2.5"
              style={{ background: 'var(--cty-bg-elevated)' }}
            >
              <p className="text-sm text-foreground" style={fontStyle}>
                {msg.content}
              </p>
            </div>
          </div>
        );
      }

      const progress = generationProgress[msg.id];
      if (!progress) {
        return (
          <div key={msg.id} className="flex items-start py-2">
            <div
              className="max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-2.5"
              style={{ background: 'var(--cty-bg-elevated)' }}
            >
              <p className="text-sm text-default-600" style={fontStyle}>{msg.content}</p>
            </div>
          </div>
        );
      }

      return (
        <div key={msg.id} className="flex flex-col items-start gap-2 py-2">
          <div
            className="w-full bg-[#F9FAFB] rounded-xl p-4 flex flex-col gap-3"
            style={{ boxShadow: '0px 2px 2px -1px rgba(10,13,18,0.04), 0px 4px 6px -2px rgba(10,13,18,0.03), 0px 12px 16px -4px rgba(10,13,18,0.08)' }}
          >
            {/* Status row */}
            <div className="flex items-center justify-between gap-2">
              {progress.status === 'in_progress' ? (
                <div className="flex items-center gap-2">
                  <Icon icon="eos-icons:loading" className="text-default-600 animate-spin" width={16} />
                  <span className="text-sm text-[#333]" style={fontStyle}>Working...</span>
                </div>
              ) : progress.status === 'done' ? (
                <div className="flex items-center gap-2">
                  <Icon icon="material-symbols:check-circle-rounded" className="text-[#17B26A]" width={16} />
                  <span className="text-sm font-semibold text-[#17B26A]" style={fontStyle}>Done</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Icon icon="material-symbols:error-outline" className="text-danger" width={16} />
                  <span className="text-sm font-semibold text-danger" style={fontStyle}>Failed</span>
                </div>
              )}

              {progress.status === 'in_progress' && (
                <span className="px-2 py-0.5 rounded-full border text-xs font-medium" style={{ ...fontStyle, background: '#FFFAEB', borderColor: '#FEDF89', color: '#B54708' }}>
                  In progress
                </span>
              )}
              {progress.status === 'done' && (
                <span className="px-2 py-0.5 rounded-full border text-xs font-medium" style={{ ...fontStyle, background: '#ECFDF3', borderColor: '#ABEFC6', color: '#067647' }}>
                  Completed
                </span>
              )}
              {progress.status === 'error' && (
                <span className="px-2 py-0.5 rounded-full border text-xs font-medium" style={{ ...fontStyle, background: '#FEF3F2', borderColor: '#FECDCA', color: '#B42318' }}>
                  Error
                </span>
              )}
            </div>

            {/* Node progress list */}
            {progress.nodes.length > 0 && (
              <div className="flex flex-col gap-1 bg-[#F9FAFB] p-3 rounded">
                <span className="text-xs text-[#333]" style={fontStyle}>Generating form...</span>
                <ul className="flex flex-col gap-0.5 pl-1">
                  {progress.nodes.slice(-5).map((n, i) => {
                    const isDone = progress.completedNodes.includes(n);
                    return (
                      <li key={i} className="text-xs flex items-center gap-1.5" style={fontStyle}>
                        {isDone
                          ? <Icon icon="mdi:check-circle" width={13} className="text-green-500 shrink-0" />
                          : <span className="shrink-0 text-default-500">•</span>
                        }
                        <span className={`truncate ${isDone ? 'text-default-500' : 'text-[#333]'}`}>{n}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {progress.status === 'error' && progress.errorMsg && (
              <p className="text-xs text-danger" style={fontStyle}>{progress.errorMsg}</p>
            )}

            {progress.status === 'in_progress' && (
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-default-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse w-2/5" />
                </div>
              </div>
            )}

            {/* Click to view — version history */}
            {progress.status === 'done' && messageFormConfigs[msg.id] && (
              <button
                onClick={() => setViewingFormConfig(messageFormConfigs[msg.id])}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors ${
                  viewingFormConfig === messageFormConfigs[msg.id] ? 'text-primary' : 'text-primary hover:opacity-70'
                }`}
                style={fontStyle}
              >
                <Icon icon="mdi:eye-outline" width={16} />
                {viewingFormConfig === messageFormConfigs[msg.id] ? 'Currently viewing' : 'Click to view'}
              </button>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full min-h-0 bg-content1">
        {/* Messages */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3">
          {messages.map(renderMessage)}
          <div ref={scrollRef} />
        </div>

        {/* Prompt bar */}
        {!isReadOnly && (
          <div className="flex-shrink-0 px-4 pb-4 pt-2 bg-content1">
            {/* Element context chip — appears when an element is selected */}
            {interactionState.isActive && interactionState.selectedText && (
              <span
                className="inline-block self-start mb-2 px-1.5 py-0.5 rounded-[6px] border text-xs"
                style={{ background: '#FDF2F8', borderColor: '#E66DB6', color: '#97005B' }}
              >
                {interactionState.selectedText.substring(0, 30)}
              </span>
            )}
            <CtyPromptInputWithErrorBoundary
              elementType={promptInputType.TEXTAREA}
              id="formgenie-chatinput"
              classNames={{
                inputWrapper: '!bg-content1 shadow-none border border-default-200 rounded-[10px] px-4 pt-3 pb-2',
                innerWrapper: 'relative',
                input: 'text-sm p-0',
              }}
              radius="none"
              variant="flat"
              handleSendClick={(text: string) => {
                setPendingInstructions('');
                handleSendClick(text);
              }}
              prefillText={isEdit ? pendingInstructions : ''}
              placeholder="Describe the form you need, or paste a Universal Schema JSON..."
              enableSpeak={false}
              enableSendBtn={true}
              setIsSpeechMode={setIsSpeechMode}
              isSpeechMode={isSpeechMode}
              enableMention={false}
              enableActionMention={false}
              enableFile={false}
              enableImage={false}
              enableTranslation={false}
              enableSearch={false}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Save Modal */}
      <Modal isOpen={isSaveModalOpen} onClose={() => setIsSaveModalOpen(false)} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span>Save Form</span>
            <span className="text-sm font-normal text-default-500">Enter a name for this form</span>
          </ModalHeader>
          <ModalBody>
            <Input
              label="Form Name"
              placeholder={`e.g., ${worker?.name || formConfig?.formName || 'Form'} 1`}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              variant="bordered"
              description={formName.trim() ? '' : `If left empty, will be named "${worker?.name || 'Form'} [number]"`}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmSave(); }}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setIsSaveModalOpen(false)}>Cancel</Button>
            <Button color="primary" onPress={confirmSave} isLoading={isSaving}>
              {formName.trim() ? 'Save' : 'Save with Auto-name'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Split layout — form preview LEFT (62%), chat RIGHT (38%); chat hidden in read-only mode */}
      <div className="flex-1 min-h-0 overflow-hidden flex">
        <div className={`min-h-0 min-w-0 ${isReadOnly ? 'flex-1' : 'flex-[62]'}`}>
          {renderPreviewPanel()}
        </div>
        {!isReadOnly && (
          <div className="flex-[38] min-h-0 min-w-0 border-l border-default-200">
            {renderChatPanel()}
          </div>
        )}
      </div>
    </>
  );
}

export default FormGenieSplitPanel;
